/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import debounce from "lodash/debounce";
import isNaN from "lodash/fp/isNaN";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  DASHBOARD_GLANCE_HEIGHT_DESKTOP,
  DASHBOARD_GLANCE_HEIGHT_MOBILE,
  DASHBOARD_GLANCE_HEIGHT_TABLET,
  DEVICE_BP_TABLET_MIN,
  PAGE_CONTENT_MAX_WIDTH,
  ScrollStatus,
} from "~/constants";
import { FIXED_HEADER_HEIGHT, HEADER_SCROLL_OFFSET, PAGE_HEADER_HEIGHT } from "~/features/header/constants";

const getPageHeaderOffset = (width: number) => {
  const defaultOffset = PAGE_HEADER_HEIGHT - FIXED_HEADER_HEIGHT;

  if (width >= PAGE_CONTENT_MAX_WIDTH) {
    return defaultOffset;
  } else if (width < PAGE_CONTENT_MAX_WIDTH && width > DEVICE_BP_TABLET_MIN) {
    return 0;
  } else if (width <= DEVICE_BP_TABLET_MIN) {
    return 0;
  }

  return defaultOffset;
};

const getDashboardPageHeaderOffset = (width: number, onDashboard: boolean) => {
  const defaultOffset = PAGE_HEADER_HEIGHT - FIXED_HEADER_HEIGHT;

  if (width >= PAGE_CONTENT_MAX_WIDTH) {
    return onDashboard ? defaultOffset + DASHBOARD_GLANCE_HEIGHT_DESKTOP : defaultOffset;
  } else if (width < PAGE_CONTENT_MAX_WIDTH && width > DEVICE_BP_TABLET_MIN) {
    return onDashboard ? DASHBOARD_GLANCE_HEIGHT_TABLET : 0;
  } else if (width <= DEVICE_BP_TABLET_MIN) {
    return onDashboard ? DASHBOARD_GLANCE_HEIGHT_MOBILE : 0;
  }

  return defaultOffset;
};

const useScrollStatus = () => {
  const [scrollStatus, setScrollStatus] = useState<ScrollStatus>(ScrollStatus.INITIAL);
  const [scrollHeaderPercent, setScrollHeaderPercent] = useState(0);

  const location = useLocation();
  const currentLocation = location.pathname;
  const isOnDashboard = currentLocation === "/app/dashboard";

  useEffect(() => {
    const scrollListener = debounce(
      () => {
        const { scrollY, innerWidth } = window;
        const pageHeaderOffset = getPageHeaderOffset(innerWidth);
        const dashboardPageHeaderOffset = getDashboardPageHeaderOffset(innerWidth, isOnDashboard);

        const percent = scrollY / pageHeaderOffset;
        const validPercent = !isNaN(percent) ? percent : 0;
        setScrollHeaderPercent(validPercent > 1 ? 1 : validPercent < 0 ? 0 : validPercent);

        if (scrollY <= HEADER_SCROLL_OFFSET) {
          setScrollStatus(ScrollStatus.INITIAL);
        } else if (scrollY > HEADER_SCROLL_OFFSET && scrollY <= dashboardPageHeaderOffset) {
          setScrollStatus(ScrollStatus.PAGE_HEADER);
        } else if (scrollY > dashboardPageHeaderOffset) {
          setScrollStatus(ScrollStatus.CONTENT);
        }
      },
      50,
      { leading: false, maxWait: 50 },
    );

    window.addEventListener("scroll", scrollListener);

    scrollListener();

    return () => {
      window.removeEventListener("scroll", scrollListener);
      scrollListener.cancel();
    };
  }, [isOnDashboard]);

  return { scrollStatus, scrollHeaderPercent };
};

export default useScrollStatus;
